import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { proxiedImage, onImgError } from '../utils/images';

interface Brand {
  slug: string;
  name: string;
  products: number;
  image: string;
}

const BrandsPage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    fetch('/api/brands')
      .then((r) => r.json())
      .then((d) => { setBrands(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = q
    ? brands.filter((b) => b.name.toLowerCase().includes(q.toLowerCase()))
    : brands;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Brands on GoodCart',
    numberOfItems: brands.length,
    itemListElement: brands.slice(0, 50).map((b, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: b.name,
      url: `https://goodcart.org/brands/${b.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-cream-100">
      <SEO
        title="Brands"
        description="Shop trusted clean-ingredient brands on GoodCart. Every brand is vetted for quality and honest labels."
        image="/og-image.png"
        url="https://goodcart.org/brands"
        type="website"
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <section className="bg-sage-700 text-cream-50">
        <div className="container-custom py-6 md:py-8">
          <p className="eyebrow text-sage-200 mb-2">Directory</p>
          <h1 className="text-2xl md:text-3xl font-display text-cream-50">Brands we trust</h1>
          <p className="mt-2 max-w-2xl text-sm text-sage-100/90">
            {brands.length} brands, each vetted for clean ingredients and honest labels.
          </p>
        </div>
      </section>

      <div className="container-custom py-8">
        <div className="max-w-md mb-8">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter brands..."
            aria-label="Filter brands"
            className="w-full py-2.5 px-4 bg-white border border-earth-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sage-500"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((b, i) => (
              <motion.div
                key={b.slug}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.3) }}
              >
                <Link
                  to={`/brands/${b.slug}`}
                  className="organic-card flex items-center gap-4 p-4 group h-full"
                >
                  <div className="h-14 w-14 rounded-xl bg-white border border-earth-100 overflow-hidden flex-shrink-0">
                    {b.image ? (
                      <img src={proxiedImage(b.image)} alt="" loading="lazy" decoding="async"
                        className="w-full h-full object-contain p-1.5" onError={onImgError()} />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center font-display text-xl text-sage-600">
                        {b.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-earth-900 truncate group-hover:text-sage-700 transition-colors">{b.name}</p>
                    <p className="text-xs text-earth-500 tabular-nums">{b.products} {b.products === 1 ? 'product' : 'products'}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandsPage;
